/**
 * Generato automaticamente.
 * framework-cfg-utenti
 */
import { useMutation, useQuery } from 'react-query';
import type {
  MutationFunction,
  QueryFunction,
  QueryKey,
  UseMutationOptions,
  UseQueryOptions,
  UseQueryResult,
} from 'react-query';
import { apiClientRequest } from '../../../client/apiClient';
import type { ErrorType } from '../../../client/apiClient';
import type { Abilitazione } from '.././schemas';

type AwaitedInput<T> = PromiseLike<T> | T;

type Awaited<O> = O extends AwaitedInput<infer T> ? T : never;

// eslint-disable-next-line
type SecondParameter<T extends (...args: any) => any> = T extends (
  config: any,
  args: infer P,
) => any
  ? P
  : never;

/**
 * Restituisce abilitazione
 * @summary Restituisce abilitazione
 */
export const getAbilitazioniId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Abilitazione>(
    { url: `/framework-cfg-utenti/abilitazioni/${id}`, method: 'get', signal },
    options,
  );
};

export const getGetAbilitazioniIdQueryKey = (id: string) =>
  [`/framework-cfg-utenti/abilitazioni/${id}`] as const;

export const getGetAbilitazioniIdQueryOptions = <
  TData = Awaited<ReturnType<typeof getAbilitazioniId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getAbilitazioniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryOptions<
  Awaited<ReturnType<typeof getAbilitazioniId>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAbilitazioniIdQueryKey(id);

  const queryFn: QueryFunction<
    Awaited<ReturnType<typeof getAbilitazioniId>>
  > = ({ signal }) => getAbilitazioniId(id, requestOptions, signal);

  return { queryKey, queryFn, enabled: !!id, ...queryOptions };
};

export type GetAbilitazioniIdQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAbilitazioniId>>
>;
export type GetAbilitazioniIdQueryError = ErrorType<unknown>;

/**
 * @summary Restituisce abilitazione
 */
export const useGetAbilitazioniId = <
  TData = Awaited<ReturnType<typeof getAbilitazioniId>>,
  TError = ErrorType<unknown>,
>(
  id: string,
  options?: {
    query?: UseQueryOptions<
      Awaited<ReturnType<typeof getAbilitazioniId>>,
      TError,
      TData
    >;
    request?: SecondParameter<typeof apiClientRequest>;
  },
): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetAbilitazioniIdQueryOptions(id, options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Aggiorna abilitazione
 * @summary Aggiorna abilitazione
 */
export const putAbilitazioniId = (
  id: string,
  abilitazione: Abilitazione,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<Abilitazione>(
    {
      url: `/framework-cfg-utenti/abilitazioni/${id}`,
      method: 'put',
      headers: { 'Content-Type': 'application/json' },
      data: abilitazione,
    },
    options,
  );
};

export const getPutAbilitazioniIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putAbilitazioniId>>,
    TError,
    { id: string; data: Abilitazione },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof putAbilitazioniId>>,
  TError,
  { id: string; data: Abilitazione },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof putAbilitazioniId>>,
    { id: string; data: Abilitazione }
  > = props => {
    const { id, data } = props ?? {};

    return putAbilitazioniId(id, data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PutAbilitazioniIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof putAbilitazioniId>>
>;
export type PutAbilitazioniIdMutationBody = Abilitazione;
export type PutAbilitazioniIdMutationError = ErrorType<unknown>;

/**
 * @summary Aggiorna abilitazione
 */
export const usePutAbilitazioniId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof putAbilitazioniId>>,
    TError,
    { id: string; data: Abilitazione },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPutAbilitazioniIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Cancellazione abilitazione
 * @summary Cancellazione abilitazione
 */
export const deleteAbilitazioniId = (
  id: string,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    { url: `/framework-cfg-utenti/abilitazioni/${id}`, method: 'delete' },
    options,
  );
};

export const getDeleteAbilitazioniIdMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteAbilitazioniId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof deleteAbilitazioniId>>,
  TError,
  { id: string },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof deleteAbilitazioniId>>,
    { id: string }
  > = props => {
    const { id } = props ?? {};

    return deleteAbilitazioniId(id, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type DeleteAbilitazioniIdMutationResult = NonNullable<
  Awaited<ReturnType<typeof deleteAbilitazioniId>>
>;

export type DeleteAbilitazioniIdMutationError = ErrorType<unknown>;

/**
 * @summary Cancellazione abilitazione
 */
export const useDeleteAbilitazioniId = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof deleteAbilitazioniId>>,
    TError,
    { id: string },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getDeleteAbilitazioniIdMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Restituisce tutti i tipi di abilitazione che possono essere associate ad un ruolo
 * @summary Elenco tipi di abilitazioni
 */
export const getAbilitazioni = (
  options?: SecondParameter<typeof apiClientRequest>,
  signal?: AbortSignal,
) => {
  return apiClientRequest<Abilitazione[]>(
    { url: `/framework-cfg-utenti/abilitazioni`, method: 'get', signal },
    options,
  );
};

export const getGetAbilitazioniQueryKey = () =>
  [`/framework-cfg-utenti/abilitazioni`] as const;

export const getGetAbilitazioniQueryOptions = <
  TData = Awaited<ReturnType<typeof getAbilitazioni>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAbilitazioni>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryOptions<
  Awaited<ReturnType<typeof getAbilitazioni>>,
  TError,
  TData
> & { queryKey: QueryKey } => {
  const { query: queryOptions, request: requestOptions } = options ?? {};

  const queryKey = queryOptions?.queryKey ?? getGetAbilitazioniQueryKey();

  const queryFn: QueryFunction<Awaited<ReturnType<typeof getAbilitazioni>>> = ({
    signal,
  }) => getAbilitazioni(requestOptions, signal);

  return { queryKey, queryFn, ...queryOptions };
};

export type GetAbilitazioniQueryResult = NonNullable<
  Awaited<ReturnType<typeof getAbilitazioni>>
>;
export type GetAbilitazioniQueryError = ErrorType<unknown>;

/**
 * @summary Elenco tipi di abilitazioni
 */
export const useGetAbilitazioni = <
  TData = Awaited<ReturnType<typeof getAbilitazioni>>,
  TError = ErrorType<unknown>,
>(options?: {
  query?: UseQueryOptions<
    Awaited<ReturnType<typeof getAbilitazioni>>,
    TError,
    TData
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseQueryResult<TData, TError> & { queryKey: QueryKey } => {
  const queryOptions = getGetAbilitazioniQueryOptions(options);

  const query = useQuery(queryOptions) as UseQueryResult<TData, TError> & {
    queryKey: QueryKey;
  };

  query.queryKey = queryOptions.queryKey;

  return query;
};

/**
 * Crea abilitazione se non esiste
 * @summary Creazione abilitazione
 */
export const postAbilitazioni = (
  abilitazione: Abilitazione,
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<Abilitazione>(
    {
      url: `/framework-cfg-utenti/abilitazioni`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: abilitazione,
    },
    options,
  );
};

export const getPostAbilitazioniMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAbilitazioni>>,
    TError,
    { data: Abilitazione },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAbilitazioni>>,
  TError,
  { data: Abilitazione },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAbilitazioni>>,
    { data: Abilitazione }
  > = props => {
    const { data } = props ?? {};

    return postAbilitazioni(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAbilitazioniMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAbilitazioni>>
>;
export type PostAbilitazioniMutationBody = Abilitazione;
export type PostAbilitazioniMutationError = ErrorType<unknown>;

/**
 * @summary Creazione abilitazione
 */
export const usePostAbilitazioni = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAbilitazioni>>,
    TError,
    { data: Abilitazione },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostAbilitazioniMutationOptions(options);

  return useMutation(mutationOptions);
};
/**
 * Importa una serie di abilitazioni in modalità insertOrUpdate
 * @deprecated
 * @summary Importa una serie di abilitazioni
 */
export const postAbilitazioniImport = (
  abilitazione: Abilitazione[],
  options?: SecondParameter<typeof apiClientRequest>,
) => {
  return apiClientRequest<void>(
    {
      url: `/framework-cfg-utenti/abilitazioni/import`,
      method: 'post',
      headers: { 'Content-Type': 'application/json' },
      data: abilitazione,
    },
    options,
  );
};

export const getPostAbilitazioniImportMutationOptions = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAbilitazioniImport>>,
    TError,
    { data: Abilitazione[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}): UseMutationOptions<
  Awaited<ReturnType<typeof postAbilitazioniImport>>,
  TError,
  { data: Abilitazione[] },
  TContext
> => {
  const { mutation: mutationOptions, request: requestOptions } = options ?? {};

  const mutationFn: MutationFunction<
    Awaited<ReturnType<typeof postAbilitazioniImport>>,
    { data: Abilitazione[] }
  > = props => {
    const { data } = props ?? {};

    return postAbilitazioniImport(data, requestOptions);
  };

  return { mutationFn, ...mutationOptions };
};

export type PostAbilitazioniImportMutationResult = NonNullable<
  Awaited<ReturnType<typeof postAbilitazioniImport>>
>;
export type PostAbilitazioniImportMutationBody = Abilitazione[];
export type PostAbilitazioniImportMutationError = ErrorType<unknown>;

/**
 * @deprecated
 * @summary Importa una serie di abilitazioni
 */
export const usePostAbilitazioniImport = <
  TError = ErrorType<unknown>,
  TContext = unknown,
>(options?: {
  mutation?: UseMutationOptions<
    Awaited<ReturnType<typeof postAbilitazioniImport>>,
    TError,
    { data: Abilitazione[] },
    TContext
  >;
  request?: SecondParameter<typeof apiClientRequest>;
}) => {
  const mutationOptions = getPostAbilitazioniImportMutationOptions(options);

  return useMutation(mutationOptions);
};
